import React, { useState, useEffect, useCallback } from 'react'
import { InitiativeManagementState } from './store/State'
import InitiativeManagementService from './Service'
import { SessionManagerState } from 'smart-components/session-manager/store/State'
import SessionManagerService from '../session-manager/Service'
import { useSnackbar } from 'notistack'
import { IInitiativeManagementProviderProps } from './Interfaces'
import { AUTHENTICATION_SERVICE } from '../../config/constants/env'

const Provider: React.FC<IInitiativeManagementProviderProps> = ({ children, enterpriseId, submissionLinkCode }) => {
  const [state, setState] = useState<InitiativeManagementState>(InitiativeManagementService.state)
  const [sessionState, setSessionState] = useState<SessionManagerState>(SessionManagerService.state)

  const { enqueueSnackbar } = useSnackbar()

  const goToSettings = () => {
    setTimeout(() => {
      window.location.href = `${AUTHENTICATION_SERVICE.WEB_URL}/settings`
    }, 300)
  }

  const onErrorCallback = useCallback(
    (error: string) => {
      enqueueSnackbar(error, {
        variant: 'warning',
        anchorOrigin: {
          vertical: 'top',
          horizontal: 'right'
        }
      })
    },
    [enqueueSnackbar]
  )

  const onSuccessCallback = useCallback(
    (message: string) => {
      enqueueSnackbar(message, {
        variant: 'success',
        anchorOrigin: {
          vertical: 'top',
          horizontal: 'right'
        }
      })
    },
    [enqueueSnackbar]
  )

  useEffect(() => {
    InitiativeManagementService.addListener(InitiativeManagementService.Events.onStateChange, setState)
    SessionManagerService.addListener(SessionManagerService.Events.onStateChange, setSessionState)
    return () => {
      InitiativeManagementService.removeListener(InitiativeManagementService.Events.onStateChange, setState)
      SessionManagerService.removeListener(SessionManagerService.Events.onStateChange, setSessionState)
      InitiativeManagementService.reset()
    }
  }, [])

  useEffect(() => {
    async function loadInitiative() {
      const currentUserInitiatives = SessionManagerService.state?.currentUser?.initiatives || []

      const initiative = currentUserInitiatives.find(
        it => it.enterprise_id === enterpriseId && it.submission_link_code === submissionLinkCode
      )

      if (initiative) {
        const error = await InitiativeManagementService.getInitiativeData(enterpriseId, initiative.initiative_id)

        if (error) {
          onErrorCallback(error)
        }
      }
    }

    loadInitiative()
  }, [onErrorCallback, enterpriseId, submissionLinkCode])

  const submitChanges = useCallback(async () => {
    const [user, error] = await InitiativeManagementService.submitChanges()

    if (error) {
      onErrorCallback(error)
      return
    }

    SessionManagerService.setCurrentUser(user)
    onSuccessCallback('Changes submitted successfully')
    goToSettings()
  }, [onErrorCallback, onSuccessCallback])

  const withdrawFromInitiative = useCallback(async () => {
    const error = await InitiativeManagementService.withdrawFromInitiative()

    if (error) {
      onErrorCallback(error)
      return
    }

    onSuccessCallback('Initiative information deleted successfully')
    goToSettings()
  }, [onErrorCallback, onSuccessCallback])

  const withdrawFromEnterprise = useCallback(async () => {
    const error = await InitiativeManagementService.withdrawFromEnterprise()

    if (error) {
      onErrorCallback(error)
      return
    }

    onSuccessCallback(`All information removed from enterprise successfully`)
    goToSettings()
  }, [onErrorCallback, onSuccessCallback])

  const hasMoreThanOneInitiativeOnEnterprise = (sessionState.currentUser?.initiatives || []).some(
    item => item.enterprise_id === enterpriseId && item.submission_link_code !== submissionLinkCode
  )
  console.log({ hasMoreThanOneInitiativeOnEnterprise }, { submissionLinkCode, enterpriseId }, sessionState.currentUser)

  return (
    <React.Fragment>
      {children({
        state,
        submitChanges,
        setUndergraduateGpaValue: InitiativeManagementService.setUndergraduateGpaValue.bind(
          InitiativeManagementService
        ),
        setDoesNotApplyForUndergraduateGpa: InitiativeManagementService.setDoesNotApplyForUndergraduateGpa.bind(
          InitiativeManagementService
        ),
        setGraduateGpaValue: InitiativeManagementService.setGraduateGpaValue.bind(InitiativeManagementService),
        setDoesNotApplyForGraduateGpa: InitiativeManagementService.setDoesNotApplyForGraduateGpa.bind(
          InitiativeManagementService
        ),
        setQuestionAnswer: InitiativeManagementService.setQuestionAnswer.bind(InitiativeManagementService),
        setQuestionChoice: InitiativeManagementService.setQuestionChoice.bind(InitiativeManagementService),
        withdrawHandler: hasMoreThanOneInitiativeOnEnterprise ? withdrawFromInitiative : withdrawFromEnterprise,
        withdrawButtonText: hasMoreThanOneInitiativeOnEnterprise
          ? 'Withdraw from initiative'
          : 'Withdraw from enterprise',
        withdrawConfirmationMessage: hasMoreThanOneInitiativeOnEnterprise
          ? 'Do you confirm the withdraw from initiative?'
          : 'Do you confirm the withdraw from enterprise?'
      })}
    </React.Fragment>
  )
}

export default Provider
